import { FC, ReactElement, useMemo, useState } from 'react';
import {
    MdAdminPanelSettings,
    MdBuild,
    MdCable,
    MdCode,
    MdInsights,
    MdLink,
    MdOpenInNew,
    MdTimeline,
} from 'react-icons/md';
import Button from '@srm/ui/button';
import Menu from '@srm/ui/menu';
import type { IListElement } from '@srm/ui/types/IListElement';
import { collectToolGroups, describeToolUrl, type IToolGroup, type IToolLink, type ToolIcon } from '@srm/core/config';
import type { IConfig } from '@/configuration/models/IConfig';
import { ConfigService } from '@/configuration/services/configService';
import { controlPanelBaseUrl } from '@utils/controlPanelUrl';
import './ToolsMenu.scss';

const ICONS: Record<ToolIcon, ReactElement> = {
    admin: <MdAdminPanelSettings size="24" aria-hidden />,
    integrations: <MdCable size="24" aria-hidden />,
    grafana: <MdInsights size="24" aria-hidden />,
    trace: <MdTimeline size="24" aria-hidden />,
    api: <MdCode size="24" aria-hidden />,
    link: <MdLink size="24" aria-hidden />,
};

const APPS_GROUP = 'Приложения';
const DEFAULT_INTEGRATIONS_URL = '/integrations/controlpanel/';

/** Пункты Front: первой — «Панель управления», за ней `application.tools`, без ключа — «Интеграции». */
const toolLinks = (config: IConfig): IToolLink[] => [
    { name: 'Панель управления', url: `${controlPanelBaseUrl(config)}/`, group: APPS_GROUP, icon: 'admin' },
    ...(config.application.tools ?? [
        { name: 'Интеграции', url: DEFAULT_INTEGRATIONS_URL, group: APPS_GROUP, icon: 'integrations' },
    ]),
];

/** Пункты меню: подпись группы, группа (`role="group"`), ссылки в новую вкладку, разделитель между группами. */
const toListElements = (groups: IToolGroup[]): IListElement[] =>
    groups.flatMap((group, index) => [
        { value: `group:${group.title}`, label: group.title, isTitle: true, separated: index > 0 },
        ...group.items.map<IListElement>((link) => ({
            value: link.url,
            url: link.url,
            newTab: true,
            group: group.title,
            testId: `tools-item-${link.icon ?? 'link'}`,
            label: (
                <div className="rf-tools-menu__item">
                    {ICONS[link.icon ?? 'link'] ?? ICONS.link}
                    <span className="rf-tools-menu__main">
                        <span>{link.name}</span>
                        <span className="rf-tools-menu__meta">{link.description || describeToolUrl(link.url)}</span>
                    </span>
                    {/* не endAdornment: его обёртка гасит щелчок, и по значку ссылка бы не открывалась */}
                    <MdOpenInNew
                        className="rf-tools-menu__external"
                        size="16"
                        aria-hidden
                        data-testid="tools-item-external"
                    />
                </div>
            ),
        })),
    ]);

/**
 * Кнопка «Инструменты» в шапке для админов и вне PROD: панель управления, интеграции и прочие инструменты стенда —
 * как гаечный ключ в шапке админки. Ссылки открываются в новой вкладке; `nonProdOnly` на PROD не показываются.
 */
const ToolsMenu: FC = () => {
    const [isOpen, setIsOpen] = useState(false);

    const items = useMemo(() => {
        const config = ConfigService.get();
        return toListElements(collectToolGroups(toolLinks(config), config.environment));
    }, []);

    return (
        <div className="rf-tools-menu">
            <Menu
                list={items}
                position="bottom-end"
                width={320}
                opened={isOpen}
                onOpenChange={setIsOpen}
                popoverProps={{ listClassName: 'rf-tools-menu__list' }}
            >
                <Button
                    variant="header"
                    size="md"
                    data-testid="tools-menu-button"
                    aria-label="Инструменты"
                    title="Инструменты"
                    startAdornment={<MdBuild size="22" />}
                />
            </Menu>
        </div>
    );
};

export default ToolsMenu;
